import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import api from '../../../services/api';

interface UserOption {
  id: number;
  userDisplayName?: string | null;
  userFirstName?: string;
  userLastName?: string;
  userEmail?: string;
}

interface TemplateFolderShareRow {
  shareId: number;
  sharedWithUserId: number;
  sharedWithUserName: string;
  includeSubfolders: boolean;
  permission: 'read' | 'write';
}

interface ShareTemplateFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folderId: number | null;
  folderName: string;
  onShared: () => void;
}

const ShareTemplateFolderModal: React.FC<ShareTemplateFolderModalProps> = ({
  isOpen,
  onClose,
  folderId,
  folderName,
  onShared,
}) => {
  const { colors } = useTheme();
  const [users, setUsers] = useState<UserOption[]>([]);
  const [existingShares, setExistingShares] = useState<TemplateFolderShareRow[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [includeSubfolders, setIncludeSubfolders] = useState(true);
  const [permission, setPermission] = useState<'read' | 'write'>('read');
  const [saving, setSaving] = useState(false);
  const [revokingId, setRevokingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || folderId == null) return;
    setError(null);
    setUsersLoading(true);
    const load = async () => {
      try {
        const [usersRes, sharesRes] = await Promise.all([
          api.get<UserOption[]>('/users'),
          api.get<TemplateFolderShareRow[]>(`/emails/template-folders/${folderId}/shares`),
        ]);
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
        const shares = Array.isArray(sharesRes.data) ? sharesRes.data : [];
        setExistingShares(shares);
        setSelectedUserIds([]);
        setIncludeSubfolders(true);
        setPermission('read');
      } catch {
        setUsers([]);
        setExistingShares([]);
      } finally {
        setUsersLoading(false);
      }
    };
    void load();
  }, [isOpen, folderId]);

  const toggleUser = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  const userLabel = (u: UserOption) =>
    u.userDisplayName?.trim() ||
    [u.userFirstName, u.userLastName].filter(Boolean).join(' ') ||
    u.userEmail ||
    `User #${u.id}`;

  const alreadySharedIds = new Set(existingShares.map((s) => s.sharedWithUserId));

  const handleSubmit = async () => {
    if (folderId == null || selectedUserIds.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      for (const sharedWithUserId of selectedUserIds) {
        await api.post(`/emails/template-folders/${folderId}/share`, {
          sharedWithUserId,
          includeSubfolders,
          permission,
        });
      }
      onShared();
      onClose();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Failed to share folder');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (shareId: number) => {
    setRevokingId(shareId);
    setError(null);
    try {
      await api.delete(`/emails/template-folders/shares/${shareId}`);
      setExistingShares((prev) => prev.filter((s) => s.shareId !== shareId));
      onShared();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Failed to revoke share');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalHeader>
        <ModalTitle>Share template folder</ModalTitle>
        <button
          type="button"
          onClick={onClose}
          className={cn('p-1.5 rounded-lg', colors.textMuted, colors.interactiveHover)}
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </ModalHeader>
      <ModalContent>
        {folderName && (
          <div className={cn('flex items-center gap-2.5 px-4 py-3 rounded-xl mb-4 bg-black/5 dark:bg-white/5', colors.text)}>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider opacity-60">Folder</p>
              <p className="text-sm font-medium truncate">{folderName}</p>
            </div>
          </div>
        )}

        {existingShares.length > 0 && (
          <div className="mb-4">
            <h3 className={cn('text-sm font-semibold mb-2', colors.text)}>Currently shared with</h3>
            <ul className={cn('rounded-xl border divide-y overflow-hidden', colors.border)}>
              {existingShares.map((share) => (
                <li key={share.shareId} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                  <span className={cn('truncate', colors.text)}>{share.sharedWithUserName}</span>
                  <span className={cn('text-xs shrink-0', colors.textMuted)}>
                    {share.includeSubfolders ? 'incl. subfolders' : 'this folder only'}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={revokingId === share.shareId}
                    disabled={revokingId != null}
                    onClick={() => void handleRevoke(share.shareId)}
                    className="text-red-600 hover:text-red-700 shrink-0"
                  >
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <h3 className={cn('text-sm font-semibold mb-2', colors.text)}>Share with more users</h3>
            <p className={cn('text-xs mb-3', colors.textMuted)}>
              Recipients can use library items in this folder when composing new messages.
            </p>

            {usersLoading ? (
              <div className={cn('flex items-center justify-center py-12', colors.textMuted)}>
                <span className="animate-pulse">Loading users…</span>
              </div>
            ) : (
              <div className={cn('rounded-xl border overflow-hidden max-h-52 overflow-y-auto', colors.border)}>
                {users
                  .filter((u) => !alreadySharedIds.has(u.id))
                  .map((u) => {
                    const selected = selectedUserIds.includes(u.id);
                    return (
                      <label
                        key={u.id}
                        className={cn(
                          'flex items-center gap-3 px-4 py-3 cursor-pointer border-b last:border-b-0',
                          colors.border,
                          selected && 'bg-indigo-50/50 dark:bg-indigo-900/20',
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleUser(u.id)}
                          className="rounded border-gray-300"
                        />
                        <span className={cn('text-sm font-medium truncate', colors.text)}>{userLabel(u)}</span>
                      </label>
                    );
                  })}
              </div>
            )}
          </div>

          <div className={cn('flex items-center justify-between px-4 py-3 rounded-xl bg-black/5 dark:bg-white/5')}>
            <div>
              <p className={cn('text-sm font-medium', colors.text)}>Include subfolders</p>
              <p className={cn('text-xs', colors.textMuted)}>Share all nested template folders</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={includeSubfolders}
                onChange={(e) => setIncludeSubfolders(e.target.checked)}
                className="sr-only peer"
              />
              <div
                className={cn(
                  'relative w-11 h-6 rounded-full peer-checked:bg-indigo-600',
                  'bg-gray-200 dark:bg-gray-700',
                  "after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:rounded-full after:h-5 after:w-5 after:bg-white after:shadow-sm after:transition-all peer-checked:after:translate-x-5",
                )}
              />
            </label>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-xl px-4 py-3 text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
            {error}
          </div>
        )}
      </ModalContent>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Close
        </Button>
        <Button
          variant="default"
          onClick={() => void handleSubmit()}
          disabled={saving || selectedUserIds.length === 0 || folderId == null}
        >
          {saving ? 'Sharing…' : `Share with ${selectedUserIds.length} user${selectedUserIds.length !== 1 ? 's' : ''}`}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ShareTemplateFolderModal;
